import { useLocalStorage } from "@mantine/hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { env } from "@/config/env";
import { defaultCurrency } from "@/lib/currency/getCurrency";
import { isDateInRange } from "@/utils/filterDateByRange";
import { getTransactionId } from "@/utils/transactionRecord";
import { filterTransactionsBySearch } from "@/utils/transactionSearch";

import type { TransactionQueryParams } from "@/services/auctions/types";
import type { AuctionCarTransaction } from "@/types/auctions";
import type { Currency } from "@/types/currency";
import type { DatesRange, PaginatedList } from "@/types/entity";

type TransactionsQueryResult = {
  data?: PaginatedList<AuctionCarTransaction>;
  isLoading: boolean;
  isPending?: boolean;
};

type UseTransactionsHook = (
  options: TransactionQueryParams & {
    enabled?: boolean;
    marketCarId: string;
  }
) => TransactionsQueryResult;

type UseTransactionsTableDataOptions = {
  loadAll?: boolean;
  marketCarId: string;
  sort?: TransactionQueryParams["sort"];
  useTransactions: UseTransactionsHook;
};

export function useTransactionsTableData({
  loadAll = false,
  marketCarId,
  sort,
  useTransactions,
}: UseTransactionsTableDataOptions) {
  const { t } = useTranslation();
  const isClientPagination = env.PAGINATION === "client";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRangeState] = useState<DatesRange>();
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [showOutliers, setShowOutliers] = useState(true);
  const [showReplicas, setShowReplicas] = useState(true);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const isFiltering = search.trim().length > 0;

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { data: paginatedData, isLoading: isPaginatedLoading } = useTransactions({
    marketCarId,
    page,
    pageSize,
    parameters: { full_text: "" },
    sort,
    enabled: !isClientPagination && !isFiltering && !loadAll,
  });

  const { data: allData, isLoading: isAllLoading } = useTransactions({
    marketCarId,
    page: 1,
    pageSize: 999999,
    parameters: { full_text: "" },
    sort,
    enabled: isClientPagination || isFiltering || loadAll,
  });

  const isLoading =
    isClientPagination || loadAll
      ? isAllLoading
      : isFiltering
        ? isAllLoading || !allData
        : isPaginatedLoading;

  const sourceResults = useMemo(() => {
    if (isClientPagination || isFiltering || loadAll) {
      return allData?.results ?? [];
    }

    return paginatedData?.results ?? [];
  }, [allData?.results, isClientPagination, isFiltering, loadAll, paginatedData?.results]);

  const conditionAndResultFilteredTransactions = useMemo(() => {
    return sourceResults.filter((transaction) => {
      if (selectedConditions.length > 0 && !selectedConditions.includes(transaction.condition)) {
        return false;
      }
      const effectiveResult =
        transaction.result ?? (transaction.status === "sold" ? ("sold" as const) : undefined);
      if (selectedResults.length > 0 && !effectiveResult) {
        return false;
      }
      if (
        selectedResults.length > 0 &&
        effectiveResult &&
        !selectedResults.includes(effectiveResult)
      ) {
        return false;
      }
      return true;
    });
  }, [sourceResults, selectedConditions, selectedResults]);

  const dateFilteredTransactions = useMemo(() => {
    if (!dateRange) return conditionAndResultFilteredTransactions;
    return conditionAndResultFilteredTransactions.filter((transaction) =>
      isDateInRange(transaction.date, dateRange)
    );
  }, [conditionAndResultFilteredTransactions, dateRange]);

  const attributeFilteredTransactions = dateFilteredTransactions;

  const availableConditions = useMemo(
    () =>
      [
        ...new Set(sourceResults.map((transaction) => transaction.condition).filter(Boolean)),
      ].sort(),
    [sourceResults]
  );

  const availableResults = useMemo(
    () =>
      [
        ...new Set(
          sourceResults
            .map(
              (transaction) =>
                transaction.result ??
                (transaction.status === "sold" ? ("sold" as const) : undefined)
            )
            .filter(Boolean)
        ),
      ].sort(),
    [sourceResults]
  );

  const visibilityFilteredTransactions = useMemo(() => {
    return attributeFilteredTransactions.filter((transaction) => {
      if (transaction.outlier && !showOutliers) return false;
      if (transaction.replica && !showReplicas) return false;
      return true;
    });
  }, [attributeFilteredTransactions, showOutliers, showReplicas]);

  const searchedTransactions = useMemo(() => {
    if (!isFiltering) return visibilityFilteredTransactions;
    return filterTransactionsBySearch(visibilityFilteredTransactions, search, t, currency.code);
  }, [visibilityFilteredTransactions, currency.code, isFiltering, search, t]);

  const filteredTransactions = useMemo(() => {
    if (!selectedTransactionId) return searchedTransactions;
    return searchedTransactions.filter(
      (transaction) => getTransactionId(transaction) === selectedTransactionId
    );
  }, [searchedTransactions, selectedTransactionId]);

  const tableTransactions = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredTransactions.slice(start, end);
  }, [filteredTransactions, page, pageSize]);

  const hasTableFilters =
    isFiltering ||
    !!dateRange ||
    !!selectedTransactionId ||
    selectedConditions.length > 0 ||
    selectedResults.length > 0 ||
    !showOutliers ||
    !showReplicas;
  const totalTransactions =
    hasTableFilters || isClientPagination || loadAll
      ? filteredTransactions.length
      : (paginatedData?.total ?? 0);

  function handlePageSize(nextPageSize: number) {
    setPageSize(nextPageSize);
    setPage(1);
  }

  function updateSearchQuery(query: string) {
    setSearch(query);
    setPage(1);
  }

  function setDateRange(range?: DatesRange) {
    setDateRangeState(range);
    setSelectedTransactionId(null);
    setPage(1);
  }

  function setConditionFilters(conditions: string[]) {
    setSelectedConditions(conditions);
    setSelectedTransactionId(null);
    setPage(1);
  }

  function setResultFilters(results: string[]) {
    setSelectedResults(results);
    setSelectedTransactionId(null);
    setPage(1);
  }

  function setOutliersVisibility(value: boolean) {
    setShowOutliers(value);
    setSelectedTransactionId(null);
    setPage(1);
  }

  function setReplicasVisibility(value: boolean) {
    setShowReplicas(value);
    setSelectedTransactionId(null);
    setPage(1);
  }

  function selectTransaction(transactionId: string) {
    setSelectedTransactionId((current) => (current === transactionId ? null : transactionId));
    setPage(1);
  }

  function clearAllFilters() {
    setSearch("");
    setDateRangeState(undefined);
    setSelectedConditions([]);
    setSelectedResults([]);
    setShowOutliers(true);
    setShowReplicas(true);
    setSelectedTransactionId(null);
    setPage(1);
  }

  return {
    attributeFilteredTransactions,
    availableConditions,
    availableResults,
    clearAllFilters,
    conditionAndResultFilteredTransactions,
    currency,
    dateRange,
    filteredTransactions,
    handlePageSize,
    hasSourceTransactions: sourceResults.length > 0,
    hasTableFilters,
    isFiltering,
    isLoading,
    page,
    pageSize,
    search,
    selectedConditions,
    selectedResults,
    selectedTransactionId,
    selectTransaction,
    setConditionFilters,
    setDateRange,
    setOutliersVisibility,
    setPage,
    setReplicasVisibility,
    setResultFilters,
    searchedTransactions,
    showOutliers,
    showReplicas,
    tableTransactions,
    totalTransactions,
    updateSearchQuery,
  };
}
